/**
 * @author brychter
 * @since 29/07/17
 */
import {Component, DoCheck, ElementRef, EventEmitter, Input, OnInit, Output, Renderer} from "@angular/core";
import {buildWeekdayNames, formatDate} from "./calendar-tools";

@Component({
    selector: 'calendar-view',
    templateUrl: './calendar-view.component.html'
})
export class CalendarViewComponent implements OnInit, DoCheck {
    @Input() openedCalendar;
    @Input() locale;
    @Input() format;
    @Input() position;
    @Input() disableDates;
    @Input() rangeBound: string;
    @Input() selectedDate: Date;
    @Input() period?: any;

    @Output() dateSelected = new EventEmitter();

    private today: Date;
    weeks: any[];
    current;
    weekdayNames: string[];
    public elementRef;
    private labels = {
        today: {
            "es": "HOY",
            "pt": "HOJE",
            "en": "TODAY"
        }
    };

    constructor(public render: Renderer, myElement: ElementRef) {
        let now = new Date();
        this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        this.current = {};
        this.elementRef = myElement;

    }

    preventClose($event) {
        if ($event.keyCode == 13) {
            $event.preventDefault();
            return false;
        }
    }

    ngOnInit() {
        this.weeks = [];
        for (let w = 0; w < 6; w++) {
            this.weeks.push([]);
            for (let day = 0; day < 7; day++) {
                this.weeks[w].push({});
            }
        }

        this.setCurrent(this.today);
        this.weekdayNames = buildWeekdayNames(this.locale);

        if(!!this.period) {
            if(!!this.period.from && this.rangeBound === "start") {
                this.selectedDate = this.getDateWithoutTime(this.period.from);
            }
            if(!!this.period.to && this.rangeBound === "end") {
                this.selectedDate = this.getDateWithoutTime(this.period.to);
            }
        }
    }

    private getDateWithoutTime(date:Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    disablePrevArrow = false;

    ngDoCheck() {
        if (this.disableDates) {
            if (this.current.month == (this.today.getMonth() + 1)) {
                this.disablePrevArrow = true;
            } else {
                this.disablePrevArrow = false;
            }
        }

        if(!!this.period) {
            if(!!this.period.from && this.rangeBound === "start") {
                this.selectedDate = this.getDateWithoutTime(this.period.from);
            }
            if(!!this.period.to && this.rangeBound === "end") {
                this.selectedDate = this.getDateWithoutTime(this.period.to);
            }
        }

        this.update();
    }

    prevMonth = () => {
        let d = new Date(this.current.year, this.current.month - 1, 1);
        d.setMonth(d.getMonth() - 1);
        this.setCurrent(d);
    };

    nextMonth = () => {
        let d = new Date(this.current.year, this.current.month - 1, 1);
        d.setMonth(d.getMonth() + 1);
        this.setCurrent(d);
    };

    setCurrent(d) {
        this.current = {
            month: d.getMonth() + 1,
            name: formatDate(d, 'MMMM', this.locale),
            year: d.getFullYear()
        };
        this.current.name = this.current.name[0].toLocaleUpperCase() + this.current.name.slice(1);
    }
    

    select = (value) => {
        this.selectedDate = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        this.enableMonth = true;
        let dateSelection = this.buildDateSelection(this.selectedDate);
        this.dateSelected.emit(dateSelection);
    };

    private buildDateSelection(dateSelected) : any {
        let dateSelection : any = {};
        dateSelection.rangeBound = this.rangeBound;

        dateSelection.daySelected = this.format == "text" ? this.formatDateText(dateSelected) : formatDate(dateSelected, this.format, this.locale);
        dateSelection.formattedDate = formatDate(dateSelected, 'YYYY-MM-DD', this.locale);
        dateSelection.selected = dateSelected;
        return dateSelection;
    }

    //TODO: ver si es necesario este método
    formatDateText(date) {
        return date.getDate() + " " + this.current.name.substring(0, 3) + " " + date.getFullYear();
    }

    enableMonth: boolean = true;

    update = () => {
        if (this.current) {
            let d = new Date(this.current.year, this.current.month - 1, 1);
            d.setDate(d.getDate() - d.getDay());
            let rangeStart = !!this.period && !!this.period.from? this.period.from : null;
            let rangeEnd = !!this.period && !!this.period.to? this.period.to : null;
            for (let week = 0; week < 6; week++) {
                for (let day = 0; day < 7; day++) {
                    this.weeks[week][day].date = new Date(d);
                    this.weeks[week][day].number = (100 + d.getDate()).toString().slice(1);
                    this.weeks[week][day].today = (d.getTime() === this.today.getTime());
                    this.weeks[week][day].out = ((d.getMonth() + 1) !== this.current.month);

                    if(this.isDateRange()) {
                        this.weeks[week][day].start = (!!rangeStart && rangeStart.getTime() === d.getTime());
                        this.weeks[week][day].range = (!!rangeStart && !!rangeEnd && (rangeStart.getTime() < d.getTime()) && (d.getTime() < rangeEnd.getTime()));
                        if (this.rangeBound === "end") {
                            this.weeks[week][day].disabled = !!rangeStart && rangeStart > d.getTime();
                        }
                    } else {
                        // when calendar is use on simple DatePicker, not a date range.
                        this.weeks[week][day].selected = !!this.selectedDate && (this.selectedDate.getTime() === d.getTime());
                    }

                    if (this.disableDates) {
                        this.weeks[week][day].disabled = (this.today.getTime() > d.getTime());
                    }

                    this.weeks[week][day].end = (!!rangeEnd && rangeEnd.getTime() === d.getTime());
                    d.setDate(d.getDate() + 1);
                }
            }
        }
    };

    todayLabel() {
        return this.labels.today[this.locale];
    }

    toggle() {
        this.openedCalendar = !this.openedCalendar;
    }

    hide() {
        this.openedCalendar = false;
    }

    show() {
        this.openedCalendar = true;
    }

    private isDateRange() : boolean {
        let isFromCalendar = this.rangeBound === "start";
        let isToCalendar = this.rangeBound === "end";
        return isFromCalendar || isToCalendar;
    }
}


// WEBPACK FOOTER //
// ./public/upgrade/core/datepicker/calendar-view/calendar-view.component.ts